import { useMemo, useState } from 'react';
import { detectInAppBrowser, tryOpenInSystemBrowser } from './wedgeInAppBrowser';
import { wedgeTrack } from './wedgeAnalytics';

/**
 * Shared React gate for every "Continue with Google" button outside
 * WedgeLanding (CodeRedeem, RequestAccessForm, AccessDenied, ...).
 *
 * Google 403s OAuth started inside embedded webviews (disallowed_useragent),
 * so `guardGoogle(url)` refuses to navigate when the page is inside one and
 * fires the system-browser escape instead. `notice` is a drop-in amber
 * banner to render near the button so the rep knows what happened. See
 * wedgeInAppBrowser.ts for the detection rules (installed PWA and real
 * browsers are never flagged).
 */
export function useInAppBrowserGuard(surface: string) {
  const info = useMemo(() => detectInAppBrowser(), []);
  const [escapeTried, setEscapeTried] = useState(false);

  const guardGoogle = (url: string): void => {
    if (!info.inApp) {
      window.location.assign(url);
      return;
    }
    wedgeTrack('login_inapp_google_blocked', {
      app: info.app || 'unknown',
      platform: info.platform,
      surface,
    });
    tryOpenInSystemBrowser(info);
    setEscapeTried(true);
  };

  const browserName = info.platform === 'android' ? 'Chrome' : 'Safari';
  const notice = info.inApp ? (
    <div
      style={{
        fontSize: 12.5,
        padding: '10px 12px',
        borderRadius: 10,
        background: 'rgba(245,158,11,0.12)',
        border: '1px solid rgba(245,158,11,0.40)',
        color: '#FFD380',
        lineHeight: 1.5,
        textAlign: 'left',
        marginBottom: 12,
      }}
    >
      <b>
        {info.app
          ? `You're inside ${info.app}'s built-in browser.`
          : "You're inside an app's built-in browser."}
      </b>{' '}
      Google blocks sign-in here for security. Continue with Google will try to open
      this page in {browserName} instead.
      {escapeTried && (
        <>
          {' '}
          Nothing happened? Tap the share or <b>&hellip;</b> menu and choose{' '}
          <b>Open in Browser</b>.
        </>
      )}
    </div>
  ) : null;

  return { info, escapeTried, guardGoogle, notice };
}
